import { isApiError } from "@sagot/api-client";
import { SLA_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, Field, Input, Switch, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { IntegerInput, useSlaMutations, type SlaPolicy } from "../../shared";
import { emptySlaDraft, slaBodyOf, slaDraftOf, slaProblems, type SlaDraft } from "../sla-model";

const S = "settings.sla";

type TargetKey = "next_response_minutes" | "resolution_minutes";

/** A target that is off until the checkbox is ticked; ticking it starts from a sensible value. */
function OptionalTarget({ label, field, draft, onChange }: { label: string; field: TargetKey; draft: SlaDraft; onChange: (draft: SlaDraft) => void }) {
  const { t } = useTranslation();
  const value = draft[field];
  return (
    <div className="space-y-2">
      <Checkbox label={label} checked={value !== null} onCheckedChange={(on) => { onChange({ ...draft, [field]: on === true ? draft.first_response_minutes : null }); }} />
      {value !== null ? (
        <IntegerInput aria-label={t(`${S}.minutesOf`, { target: label })} className="w-32" min={1} max={SLA_LIMITS.minutesMax} value={value} onChange={(minutes) => { onChange({ ...draft, [field]: minutes }); }} />
      ) : null}
    </div>
  );
}

interface SlaDialogProps {
  policy: SlaPolicy | null;
  onClose: () => void;
}

/** Create or edit an SLA policy: the targets in minutes, whether the clock runs only in office hours, and the early warning. */
export function SlaDialog({ policy, onClose }: SlaDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { create, update } = useSlaMutations();
  const [draft, setDraft] = useState<SlaDraft>(policy ? slaDraftOf(policy) : emptySlaDraft());
  const [checked, setChecked] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);
  const problems = checked ? slaProblems(draft) : new Set<string>();
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setChecked(true);
    if (slaProblems(draft).size > 0) return;
    try {
      const body = slaBodyOf(draft);
      if (policy) await update.mutateAsync({ id: policy.id, body });
      else await create.mutateAsync(body);
      toast.success(t(`${S}.saved`));
      onClose();
    } catch (caught) {
      if (isApiError(caught) && caught.status === 409) setNameTaken(true);
      else toast.error(message(caught));
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t(policy ? `${S}.editTitle` : `${S}.addTitle`)} description={t(`${S}.dialogHint`)} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={t(`${S}.name`)} error={problems.has("name") ? t(`${S}.nameRequired`) : nameTaken ? t(`${S}.nameTaken`) : undefined}>
            <Input maxLength={SLA_LIMITS.name} placeholder={t(`${S}.namePlaceholder`)} value={draft.name} onChange={(e) => { setNameTaken(false); setDraft({ ...draft, name: e.target.value }); }} />
          </Field>
          <Field label={t(`${S}.firstResponse`)} hint={t(`${S}.minutesHint`)}>
            <IntegerInput className="w-32" min={1} max={SLA_LIMITS.minutesMax} value={draft.first_response_minutes} onChange={(first_response_minutes) => { setDraft({ ...draft, first_response_minutes }); }} />
          </Field>
          <OptionalTarget label={t(`${S}.nextResponse`)} field="next_response_minutes" draft={draft} onChange={setDraft} />
          <OptionalTarget label={t(`${S}.resolution`)} field="resolution_minutes" draft={draft} onChange={setDraft} />
          <div className="flex items-center justify-between gap-4 rounded-md bg-surface-2 px-3 py-2.5">
            <span id="sla-office" className="text-sm font-semibold">{t(`${S}.officeHoursOnly`)}</span>
            <Switch checked={draft.office_hours_only} onCheckedChange={(office_hours_only) => { setDraft({ ...draft, office_hours_only }); }} aria-labelledby="sla-office" />
          </div>
          <Field label={t(`${S}.warnBefore`)} hint={t(`${S}.warnHint`)} error={problems.has("warn") ? t(`${S}.warnTooLong`) : undefined}>
            <IntegerInput className="w-32" min={0} max={SLA_LIMITS.minutesMax} value={draft.warn_before_minutes} onChange={(warn_before_minutes) => { setDraft({ ...draft, warn_before_minutes }); }} />
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" loading={create.isPending || update.isPending}>{t(`${S}.save`)}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
